import { DataTableColDef } from "@/lib/Components/DataTable/DataTable";
import { SettingCard } from "@/app/Settings/Settings/Components/SettingFormCard";
import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { useSettingsSupplier } from "@/app/Suppliers/Utils/useSettingsSupplier";
import { SupplierInsuranceOptionListItem } from "@/app/Suppliers/GraphQL/supplierInsuranceOptionListQuery";
import { JucyInsuranceOptionCodeCell } from "@/app/Settings/Integrations/Jucy/Components/JucyInsuranceOptionCodeCell";
import { IntegrationRecord } from "@/app/Settings/Settings/GraphQL/integrationQueries";
import { SupplierInsuranceOptionTable } from "@/app/Suppliers/Components/SupplierInsuranceOptionTable";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateIntegrationMutation } from "@/app/Settings/Settings/GraphQL/integrationMutations";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { JucyVehicleTable } from "@/app/Settings/Integrations/Jucy/Components/JucyVehicleTable";
import { JucyOfficeTable } from "@/app/Settings/Integrations/Jucy/Components/JucyOfficeTable";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";

export function JucyIntegrationScreen({
  integration,
}: {
  integration: IntegrationRecord;
}) {
  const { supplier } = useSettingsSupplier();

  const supplierInsuranceOptionCols: DataTableColDef<
    SupplierInsuranceOptionListItem,
    unknown,
    any
  >[] = [
    {
      id: "jucy",
      header: "Jucy Code",
      cell: JucyInsuranceOptionCodeCell,
    },
  ];

  const { mutateAsync } = useGqlMutation(updateIntegrationMutation);
  const metadata = integration?.metadata;

  return (
    <EditableRowsProvider>
      <div className="space-y-6">
        <SettingRowsCard title="Metadata" description="">
          <EditableSettingRow
            label="Metadata"
            value={metadata?.apiUrl || (metadata?.apiKey ? "Configured" : "")}
            editClassName="max-w-2xl"
            initialValues={{
              apiKey: metadata?.apiKey ?? "",
              apiUrl: metadata?.apiUrl ?? "",
              region: metadata?.region ?? "au",
            }}
            onSave={(values) =>
              mutateAsync({
                id: integration.id,
                input: {
                  metadata: values,
                },
              })
            }
          >
            <SelectInput
              name="region"
              label="Region"
              className="col-span-5"
              options={[
                {
                  label: "Region",
                  options: [
                    {
                      label: "au",
                      value: "au",
                    },
                    {
                      label: "nz",
                      value: "nz",
                    },
                  ],
                },
              ]}
            />
            <TextInput
              name="apiUrl"
              label="API Url"
              className="col-span-8"
              inputProps={{
                placeholder: "https://lanier.test.jucy.cloud",
              }}
            />
            <TextInput name="apiKey" label="API Key" className="col-span-8" />
          </EditableSettingRow>
        </SettingRowsCard>

        {metadata?.apiKey && metadata?.apiUrl ? (
          <>
            <SettingCard title="Vehicles" description="">
              <JucyVehicleTable />
            </SettingCard>
            <SettingCard title="Offices" description="">
              <JucyOfficeTable />
            </SettingCard>
            <SettingCard title="Insurance Options" description="">
              <WarningBanner className="mb-6">
                As the insurance options are dynamic (based on trip, vehicle,
                and driver), the insurance option will be a <b>best guess</b>{" "}
                based on the data available.
              </WarningBanner>
              <SupplierInsuranceOptionTable
                id="settings-integrations-jucy-insurance"
                supplier={supplier}
                columns={supplierInsuranceOptionCols}
              />
            </SettingCard>
          </>
        ) : (
          <WarningBanner>
            Please enter the API key and API url to continue
          </WarningBanner>
        )}
      </div>
    </EditableRowsProvider>
  );
}
